<template>
  <v-card flat color="transparent" class="bookmark-item--card d-flex flex-column">
    <BulkSelectCheckbox
      v-if="isBulkActionsActive"
      class="checkbox ml-3 mt-3"
      :checked="isSelected"
      @click="toggleSelect"
    />
    <div
      class="bookmark-thumbnail-container cursor-pointer"
      :title="bookmark.title"
      @click="onContentClick"
    >
      <BookmarkThumbnail :bookmark="bookmark" />
      <BrandButton
        v-if="!isBulkActionsActive"
        class="outgoing-link-btn"
        size="small"
        icon
        flat
        :title="t('cta.view_original_content')"
        @click.stop.prevent="openSource"
      >
        <PhArrowSquareOut class="text-h6 text-neutral900" />
      </BrandButton>
    </div>

    <div
      class="px-0 pt-3 pb-1 bookmark-headline text-body-1 font-weight-semibold text-neutral900 d-block cursor-pointer"
      @click="onContentClick"
    >
      <v-progress-circular
        v-if="bookmark.isLoading && !bookmark.hasSufficientData"
        indeterminate size="16" color="blue400" width="3"
        class="mr-1"
      />
      {{ bookmark.title ?? bookmark.domain }}
    </div>

    <div class="text-neutral600 d-flex align-center mb-3 text-caption">
      <BookmarkSource :bookmark="bookmark" />
      <span class="mx-1">&middot;</span>
      <BookmarkDate :value="bookmark.createdAt" />
    </div>

    <BookmarkTags v-if="bookmark.tags.length" class="mb-3" :tags="bookmark.tags" @tag-selected="onTagSelected" />

    <v-spacer />

    <BookmarkActions
      :key="bookmark.id" :bookmark="bookmark"
      :user-id="userId"
      :public-mode="publicMode"
      :membership="membership"
      class="text-text"
      @favorite="$emit('favorite')"
      @save="$emit('save')"
      @add-to-collection="$emit('add-to-collection')"
      @add-to-collection-from-collection="$emit('add-to-collection-from-collection')"
      @comments="$emit('comments')"
      @like="$emit('like')"
      @likes="$emit('likes')"
      @pin-edit="$emit('pin-edit')"
    />
  </v-card>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { PhArrowSquareOut } from '@phosphor-icons/vue'
import BookmarkSource from './BookmarkSource'
import BookmarkActions from './BookmarkActions'
import BookmarkDate from './BookmarkDate'
import BookmarkTags from './BookmarkTags'
import BookmarkThumbnail from './BookmarkThumbnail'
import BrandButton from '@/components/global/brand/BrandButton.vue'
import { useBulkActionsV2 } from '@/composables/useBulkActionsV2'
import BulkSelectCheckbox from '@/components/BulkSelectCheckbox.vue'

const props = defineProps({
  bookmark: {
    type: Object,
    required: true,
  },
  userId: {
    type: String,
    default: null,
  },
  membership: {
    type: Object,
    default: null,
  },
  publicMode: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'toggle-select',
  'tag-selected',
  'favorite',
  'save',
  'add-to-collection',
  'add-to-collection-from-collection',
  'comments',
  'like',
  'likes',
  'pin-edit',
])

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { isBulkActionsActive, isItemSelected } = useBulkActionsV2()

const isSelected = computed(() => isItemSelected(props.bookmark.id))

const detailRoute = computed(() => {
  if (props.publicMode) {
    const username = route.params.username
    const cid = route.params.id || props.bookmark.ownerId
    return `/u/${username}/collections/${cid}/bookmarks/${props.bookmark.id}`
  }
  if (props.bookmark.isCollectionBookmark) {
    return `/collections/${props.bookmark.ownerId}/bookmarks/${props.bookmark.id}`
  }
  return `/bookmarks/${props.bookmark.id}`
})

function onContentClick() {
  if (isBulkActionsActive.value) {
    toggleSelect()
  } else {
    router.push(detailRoute.value)
  }
}

function openSource() {
  if (!props.bookmark.brandedUrl) return
  window.open(props.bookmark.brandedUrl, '_blank', 'noopener')
}

function toggleSelect() {
  emit('toggle-select', props.bookmark)
}

function onTagSelected(tag) {
  emit('tag-selected', tag)
}
</script>

<style lang="scss" scoped>
.bookmark-item--card {
  height: 100%;

  .checkbox {
    position: absolute;
    left: 0;
    top: 0;
    z-index: 1;
  }
}

.bookmark-thumbnail-container {
  position: relative;
  display: block;

  .outgoing-link-btn {
    position: absolute;
    top: 6px;
    right: 6px;
    background-color: rgb(var(--v-theme-neutral0), 0.85) !important;
    z-index: 2;
  }
}

.bookmark-headline {
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  display: -webkit-box;
  overflow: hidden;
  word-break: break-word;
  line-height: 1.4 !important;
}
</style>
